import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import BookCard from '../components/BookCard';
import SearchBar from '../components/SearchBar';
import styles from './Discover.module.css';
import { API_BASE_URL } from '../services/api';

const Discover = () => {
  const [books, setBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [categories, setCategories] = useState(['全部']);
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // 获取分类数据
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/categories/`);
      const categoryNames = response.data.map(cat => cat.name);
      setCategories(['全部', ...categoryNames]);
    } catch (err) {
      console.error('获取分类失败:', err);
      setError('获取分类失败');
    }
  };

  // 获取小说数据
  const fetchNovels = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/novels/`);
      const novelsData = response.data.map(novel => ({
        id: novel.id,
        title: novel.title,
        author: novel.author,
        description: novel.description,
        cover_image_url: novel.cover_image_url,
        category: novel.categories.length > 0 ? novel.categories[0].name : '未分类',
        categories: novel.categories,
        is_completed: novel.is_completed,
        upload_by: novel.upload_by,
        created_at: novel.created_at,
        updated_at: novel.updated_at
      }));
      setBooks(novelsData);
    } catch (err) {
      console.error('获取小说失败:', err);
      setError('获取小说失败');
    }
  };

  // 初始化数据
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        await Promise.all([fetchCategories(), fetchNovels()]);
      } catch (err) {
        console.error('加载数据失败:', err);
        setError('加载数据失败');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (
      categoryParam &&
      categories.includes(categoryParam) &&
      selectedCategory !== categoryParam
    ) {
      setSelectedCategory(categoryParam);
    }
  }, [searchParams, categories, selectedCategory]);

  useEffect(() => {
    const searchParam = searchParams.get('search') || '';
    setSearchTerm((prev) => {
      if (prev === searchParam) {
        return prev;
      }
      return searchParam;
    });
  }, [searchParams]);

  // 筛选逻辑
  useEffect(() => {
    let filtered = books;
    
    // 按分类筛选
    if (selectedCategory !== '全部') {
      filtered = filtered.filter(book => book.category === selectedCategory);
    }
    
    // 按搜索词筛选
    if (searchTerm) {
      const loweredTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((book) => {
        const title = (book.title || '').toLowerCase();
        const author = (book.author || '').toLowerCase();
        const description = (book.description || '').toLowerCase();
        return (
          title.includes(loweredTerm) ||
          author.includes(loweredTerm) ||
          description.includes(loweredTerm)
        );
      });
    }
    
    setFilteredBooks(filtered);
  }, [books, selectedCategory, searchTerm]);

  const handleSearch = (term) => {
    const normalized = typeof term === 'string' ? term.trim() : '';
    setSearchTerm(normalized);

    const nextParams = new URLSearchParams(searchParams);
    if (normalized) {
      nextParams.set('search', normalized);
    } else {
      nextParams.delete('search');
    }
    setSearchParams(nextParams);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    const nextParams = new URLSearchParams(searchParams);
    if (category === '全部') {
      nextParams.delete('category');
    } else {
      nextParams.set('category', category);
    }
    setSearchParams(nextParams);
  };

  if (loading) {
    return (
      <div className={styles.discoverLoading}>
        <div className={styles.loadingSpinner}>加载中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.discoverError}>
        <div className={styles.errorMessage}>
          <h3>加载失败</h3>
          <p>{error}</p>
          <button 
            className={styles.retryBtn} 
            onClick={() => window.location.reload()}
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.discover}>
      <div className={styles.discoverHeader}>
        {/* <h2>发现好书</h2> */}
        <SearchBar
          onSearch={handleSearch}
          placeholder="搜索书名、作者..."
          initialValue={searchTerm}
        />
      </div>

      <div className={styles.categoriesFilter}>
        {categories.map(category => (
          <button
            key={category}
            className={`${styles.categoryBtn} ${selectedCategory === category ? styles.active : ''}`}
            onClick={() => handleCategoryChange(category)}
          >
            {category}
          </button>
        ))}
      </div>

      <div className={styles.booksGrid}>
        {filteredBooks.length > 0 ? (
          filteredBooks.map(book => (
            <BookCard 
              key={book.id} 
              book={book}
              showStatus={true}
              from="/discover"
            />
          ))
        ) : (
          <div className={styles.noBooks}>
            <p>没有找到符合条件的小说</p>
            {searchTerm && (
              <button 
                className={styles.clearSearchBtn}
                onClick={() => handleSearch('')}
              >
                清除搜索
              </button>
            )}
          </div>
        )}
      </div>

      <div className={styles.discoverStats}>
        <p>共找到 {filteredBooks.length} 本小说</p>
      </div>
    </div>
  );
};

export default Discover;
